import { logInfo, logError } from './logtail';
import { handleFetchApiError } from './apiErrorHandler';
import type { MigrationNotification } from '../types/notifications';

export interface UploadNotification {
  type: 'upload_started' | 'upload_update' | 'upload_completed' | 'upload_failed';
  upload_id: string;
  track_id: string;
  filename: string;
  file_type: string;
  status: 'processing' | 'completed' | 'failed';
  file_id?: string;
  error?: string;
  progress?: number; // Progress percentage (0-100)
}

export interface ImportNotification {
  type: 'import_started' | 'import_completed' | 'import_failed';
  import_id: string;
  target_track_id: string;
  source_track_id: string;
  source_track_name: string;
  status: 'processing' | 'completed' | 'failed';
  imported_files_count?: number;
  error?: string;
}

export interface AuthIssueNotification {
  type: 'auth_issue';
  service: string;
  error: string;
  user_id: string;
  status_code?: number;
  requires_reauth?: boolean;
}

export class AsyncUploadService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: Map<string, (notification: UploadNotification) => void> = new Map();
  private importListeners: Map<string, (notification: ImportNotification) => void> = new Map();
  private authIssueListeners: Map<string, (notification: AuthIssueNotification) => void> = new Map();
  private migrationListeners: Map<string, (notification: MigrationNotification) => void> = new Map();
  private connectionState: 'disconnected' | 'connecting' | 'connected' | 'failed' = 'disconnected';
  private baseUrl: string;
  private connectionPromise: Promise<void> | null = null;
  private connectionTimeout: NodeJS.Timeout | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    logInfo('AsyncUploadService initialized', { baseUrl });
  }

  private async getAuthToken(): Promise<string> {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('No authenticated user available');
    }
    
    const token = await user.getIdToken();
    if (!token) {
      throw new Error('Failed to get ID token');
    }
    
    return token;
  }

  connect(userId: string): Promise<void> {
    // If already connected, return existing connection
    if (this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN) {
      logInfo('WebSocket already connected, reusing existing connection', { userId });
      return Promise.resolve();
    }

    // If already connecting, return the existing promise
    if (this.connectionPromise) {
      logInfo('WebSocket connection already in progress, waiting for existing connection', { userId });
      return this.connectionPromise;
    }

    // Clear any existing connection timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    this.connectionPromise = new Promise(async (resolve, reject) => {
      try {
        this.connectionState = 'connecting';
        // Get fresh token from Firebase auth
        const token = await this.getAuthToken();
        
        const wsUrl = `${this.baseUrl.replace('http', 'ws')}/ws?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`;
        logInfo('Attempting WebSocket connection', { 
          userId, 
          baseUrl: this.baseUrl, 
          wsUrl: wsUrl,
          hasToken: !!token
        });

        // Use secure WebSocket connection with Bearer token
        this.ws = new WebSocket(wsUrl);

        // Add connection timeout
        const connectionTimeout = setTimeout(() => {
          if (this.connectionState === 'connecting') {
            logError('WebSocket connection timeout', { userId, wsUrl });
            this.connectionState = 'failed';
            if (this.ws) {
              this.ws.close();
            }
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000); // 10 second timeout

        this.ws.onopen = () => {
          clearTimeout(connectionTimeout);
          this.connectionState = 'connected';
          this.connectionPromise = null; // Clear the promise since connection is established
          logInfo('WebSocket connection opened successfully', { userId, wsUrl });
          
          // WebSocket is now connected and ready to receive notifications
          // Backend automatically registers client based on token
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            logInfo('WebSocket message received', { userId, messageType: data.type, data });
            
            // Handle authentication response
            if (data.type === 'auth_success') {
              logInfo('WebSocket authentication successful', { userId });
            } else if (data.type === 'auth_failed') {
              logError('WebSocket authentication failed', { userId, error: data.error });
              this.connectionState = 'failed';
              if (this.ws) {
                this.ws.close();
              }
              reject(new Error('WebSocket authentication failed'));
            } else {
              // Handle upload notifications (upload_started, upload_update, upload_completed, upload_failed)
              if (data.type === 'upload_started' || data.type === 'upload_update' || 
                  data.type === 'upload_completed' || data.type === 'upload_failed') {
                const notification: UploadNotification = {
                  type: data.type,
                  upload_id: data.upload_id,
                  file_id: data.file_id,
                  file_type: data.file_type,
                  filename: data.filename,
                  status: data.status,
                  track_id: data.track_id,
                  error: data.error,
                  progress: data.progress // Include progress if present
                };
                logInfo('Received upload notification', { notification, userId });
                this.handleNotification(notification);
              } else if (data.type === 'import_started' || data.type === 'import_completed' || data.type === 'import_failed') {
                // Handle import notifications
                const notification: ImportNotification = {
                  type: data.type,
                  import_id: data.import_id,
                  target_track_id: data.target_track_id,
                  source_track_id: data.source_track_id,
                  source_track_name: data.source_track_name,
                  status: data.status,
                  imported_files_count: data.imported_files_count,
                  error: data.error
                };
                logInfo('Received import notification', { notification, userId });
                this.handleImportNotification(notification);
              } else if (data.type === 'auth_issue') {
                // Handle auth issue notifications
                const notification: AuthIssueNotification = {
                  type: data.type,
                  service: data.service,
                  error: data.error,
                  user_id: data.user_id,
                  status_code: data.status_code,
                  requires_reauth: data.requires_reauth
                };
                logError('Received auth issue notification', { notification, userId });
                this.handleAuthIssue(notification);
              } else if (
                data.type === 'migration_started' ||
                data.type === 'migration_update' ||
                data.type === 'migration_completed' ||
                data.type === 'migration_failed' ||
                data.type === 'migration_cancelled'
              ) {
                const notification: MigrationNotification = {
                  type: data.type,
                  job_id: data.job_id,
                  source_provider: data.source_provider,
                  target_provider: data.target_provider,
                  status: data.status,
                  files_done: data.files_done,
                  files_total: data.files_total,
                  current_file: data.current_file,
                  error: data.error,
                };
                logInfo('Received migration notification', { notification, userId });
                this.handleMigrationNotification(notification);
              } else {
                logInfo('Received other WebSocket message', { messageType: data.type, data });
              }
            }
          } catch (error) {
            logError('Failed to parse WebSocket message', { error, data: event.data, userId });
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          const previousState = this.connectionState;
          this.connectionState = 'disconnected';
          this.connectionPromise = null; // Clear the promise
          
          logInfo('WebSocket connection closed', { 
            userId, 
            code: event.code, 
            reason: event.reason, 
            wasClean: event.wasClean,
            previousState,
            readyState: this.ws?.readyState
          });
          
          if (previousState === 'connecting') {
            // Connection was closed before it could be established
            logInfo('WebSocket connection closed before establishment', { 
              userId, 
              code: event.code, 
              reason: event.reason 
            });
            // Don't reject - just resolve with a warning that WebSocket is not available
            this.connectionState = 'failed';
            resolve();
          } else if (previousState === 'connected') {
            // Connection was established but then closed - this is unexpected
            logInfo('WebSocket connection closed after establishment', { 
              userId, 
              code: event.code, 
              reason: event.reason 
            });
            // Don't reject here, just handle reconnection
            this.handleReconnect(userId);
          } else {
            // Connection was in some other state
            this.handleReconnect(userId);
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          this.connectionState = 'failed';
          this.connectionPromise = null; // Clear the promise
          logError('WebSocket connection error', { 
            error, 
            userId, 
            wsUrl,
            connectionState: this.connectionState,
            readyState: this.ws?.readyState
          });
          // Don't reject - just resolve and continue without WebSocket
          resolve();
        };
      } catch (error) {
        this.connectionState = 'failed';
        this.connectionPromise = null; // Clear the promise
        logError('Failed to create WebSocket connection', { error, userId, baseUrl: this.baseUrl });
        // Don't reject - just resolve and continue without WebSocket
        resolve();
      }
    });

    return this.connectionPromise;
  }

  private handleReconnect(userId: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      logInfo('Attempting WebSocket reconnection', { attempt: this.reconnectAttempts, userId });
      
      setTimeout(() => {
        this.connect(userId).catch(error => {
          logError('WebSocket reconnection failed', { error, attempt: this.reconnectAttempts, userId });
        });
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      logError('Max WebSocket reconnection attempts reached', { userId });
    }
  }

  // Ensure WebSocket connection is established before uploads
  private async ensureConnection(userId: string): Promise<void> {
    if (this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    // Clear any existing connection timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    // Connect if not already connecting
    await this.connect(userId);

    // Set a timeout to disconnect after inactivity (5 minutes)
    this.connectionTimeout = setTimeout(() => {
      logInfo('WebSocket connection timeout - disconnecting due to inactivity', { userId });
      this.disconnect();
    }, 5 * 60 * 1000); // 5 minutes
  }

  private handleNotification(notification: UploadNotification) {
    // Notify all listeners
    this.listeners.forEach((listener) => {
      try {
        listener(notification);
      } catch (error) {
        logError('Error in upload notification listener', { error, notification });
      }
    });
  }

  private handleImportNotification(notification: ImportNotification) {
    // Notify all import listeners
    this.importListeners.forEach((listener) => {
      try {
        listener(notification);
      } catch (error) {
        logError('Error in import notification listener', { error, notification });
      }
    });
  }

  private handleAuthIssue(notification: AuthIssueNotification) {
    // Notify all auth issue listeners
    this.authIssueListeners.forEach((listener) => {
      try {
        listener(notification);
      } catch (error) {
        logError('Error in auth issue notification listener', { error, notification });
      }
    });
  }

  private handleMigrationNotification(notification: MigrationNotification) {
    this.migrationListeners.forEach((listener) => {
      try {
        listener(notification);
      } catch (error) {
        logError('Error in migration notification listener', { error, notification });
      }
    });
  }

  isConnected(): boolean {
    return this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  onUploadNotification(callback: (notification: UploadNotification) => void): () => void {
    const id = Math.random().toString(36).substr(2, 9);
    this.listeners.set(id, callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(id);
    };
  }

  onImportNotification(callback: (notification: ImportNotification) => void): () => void {
    const id = Math.random().toString(36).substr(2, 9);
    this.importListeners.set(id, callback);
    
    // Return unsubscribe function
    return () => {
      this.importListeners.delete(id);
    };
  }

  onAuthIssue(callback: (notification: AuthIssueNotification) => void): () => void {
    const id = Math.random().toString(36).substr(2, 9);
    this.authIssueListeners.set(id, callback);
    
    // Return unsubscribe function
    return () => {
      this.authIssueListeners.delete(id);
    };
  }

  onMigrationNotification(callback: (notification: MigrationNotification) => void): () => void {
    const id = Math.random().toString(36).substr(2, 9);
    this.migrationListeners.set(id, callback);

    return () => {
      this.migrationListeners.delete(id);
    };
  }

  async uploadFile(
    trackId: string, 
    file: File, 
    fileType: string, 
    onProgress?: (progress: number) => void,
    onChunkProgress?: (chunkIndex: number, totalChunks: number) => void
  ): Promise<{ id: string }> {
    try {
      // Check if we should use chunked upload for large files (>20MB); backend has /chunk/* routes
      const { shouldUseChunkedUpload, uploadFileChunked } = await import('./chunkUploadService');

      if (shouldUseChunkedUpload(file)) {
        logInfo('Using chunked upload for large file', {
          trackId,
          fileName: file.name,
          fileType,
          fileSize: file.size
        });

        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          await this.ensureConnection(user.uid);
        }

        const result = await uploadFileChunked(
          trackId,
          file,
          fileType as 'audio' | 'video' | 'promo' | 'artwork' | 'footages',
          { onProgress, onChunkProgress }
        );

        logInfo('Chunked upload started successfully', {
          trackId,
          fileName: file.name,
          fileType,
          taskId: result.taskId
        });

        return { id: result.taskId };
      }

      {
        logInfo('Using regular async upload for file', { 
          trackId, 
          fileName: file.name, 
          fileType, 
          fileSize: file.size 
        });
        
        // Ensure WebSocket connection is established for notifications
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          await this.ensureConnection(user.uid);
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        // Determine the correct endpoint based on file type
        let endpoint: string;
        switch (fileType) {
          case 'audio':
            endpoint = `/tracks/${trackId}/async/audio`;
            break;
          case 'video':
            endpoint = `/tracks/${trackId}/async/video`;
            break;
          case 'promo':
            endpoint = `/tracks/${trackId}/async/promo`;
            break;
          case 'artwork':
            endpoint = `/tracks/${trackId}/async/artwork`;
            break;
          case 'footages':
            endpoint = `/tracks/${trackId}/async/footages`;
            break;
          default:
            throw new Error(`Unsupported file type: ${fileType}`);
        }
        
        const token = await this.getAuthToken();
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // For async uploads, we expect 202 Accepted
        if (response.status !== 202) {
          await handleFetchApiError(response);
          throw new Error(`Upload failed: Expected 202 Accepted, got ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (!result.success || !result.taskId) {
          throw new Error(result.errorMessage || `Upload failed - no task ID received. Response: ${JSON.stringify(result)}`);
        }
        
        logInfo('Async upload started successfully', { 
          trackId, 
          fileName: file.name, 
          fileType, 
          taskId: result.taskId 
        });
        
        return { id: result.taskId };
      }
    } catch (error) {
      // Never fail silently - always log to console with full details
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Async upload service failed:', {
        trackId,
        fileName: file.name,
        fileType,
        fileSize: file.size,
        error: errorMessage,
        fullError: error,
        stack: error instanceof Error ? error.stack : undefined
      });
      
      logError('Failed to start upload', { trackId, fileName: file.name, fileType, error: errorMessage });
      throw error;
    }
  }

  async waitForCompletion(uploadId: string): Promise<UploadNotification> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Upload completion timeout'));
      }, 300000); // 5 minutes timeout
      
      const unsubscribe = this.onUploadNotification((notification) => {
        if (notification.upload_id === uploadId) {
          clearTimeout(timeout);
          unsubscribe();
          
          if (notification.status === 'completed') {
            resolve(notification);
          } else if (notification.status === 'failed') {
            reject(new Error(notification.error || 'Upload failed'));
          }
        }
      });

      // Also listen for auth issues and cancel the wait immediately
      const unsubscribeAuth = this.onAuthIssue((auth) => {
        const storageServices = new Set(['gdrive', 'dropbox', 'onedrive', 'yandex']);
        if (storageServices.has(auth.service) && (auth.requires_reauth || auth.status_code === 401)) {
          clearTimeout(timeout);
          unsubscribe();
          unsubscribeAuth();
          reject(new Error(auth.error || 'storage_auth_expired'));
        }
      });
    });
  }

  async importPromoImages(targetTrackId: string, sourceTrackId: string): Promise<{ importId: string }> {
    try {
      logInfo('Starting promo images import', { targetTrackId, sourceTrackId });
      
      // Ensure WebSocket connection is established for notifications
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        await this.ensureConnection(user.uid);
      }
      
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseUrl}/tracks/${targetTrackId}/promo/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sourceTrackId })
      });
      
      if (!response.ok) {
        await handleFetchApiError(response);
        throw new Error(`Import failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      logInfo('Backend import response', { result, targetTrackId, sourceTrackId });
      
      if (!result.success || !result.jobId) {
        throw new Error(result.errorMessage || `Import failed - no job ID received. Response: ${JSON.stringify(result)}`);
      }
      
      logInfo('Promo images import started successfully', { 
        targetTrackId, 
        sourceTrackId, 
        jobId: result.jobId 
      });
      
      return { importId: result.jobId };
    } catch (error) {
      logError('Failed to start promo images import', { targetTrackId, sourceTrackId, error });
      throw error;
    }
  }

  async waitForImportCompletion(importId: string): Promise<ImportNotification> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Import completion timeout'));
      }, 300000); // 5 minutes timeout
      
      const unsubscribe = this.onImportNotification((notification) => {
        if (notification.import_id === importId) {
          clearTimeout(timeout);
          unsubscribe();
          
          if (notification.status === 'completed') {
            resolve(notification);
          } else if (notification.status === 'failed') {
            reject(new Error(notification.error || 'Import failed'));
          }
        }
      });
    });
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.listeners.clear();
  }
}

// Create singleton instance
export const asyncUploadService = new AsyncUploadService(import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

